import React, { useEffect, useState } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ItemAdder from './ItemAdder';
import useGiftsPageAddons from '../../../../hooks/useGiftsPageAddons';
import useHolidayPricelist from '../../../../hooks/useHolidayPricelist';
import useHolidayPoses from '../../../../hooks/useHolidayPoses';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import { useCustomer } from '../../../../contexts/CustomerContext';
import styles from './HolidayBuilder.module.scss';
import useQueryString from '../../../../hooks/use-query-string';
import useStudent from '../../../../hooks/useStudent';
import useHolidayCoupon from '../../../../hooks/useHolidayCoupon';
import useCart from '../../../../hooks/useCart';
import { AddonSheet, OrderPackage } from '../../../../models';
import routes from '../../../../routes';
import useOrderPackage from '../../../../hooks/useOrderPackage';
import QUERY_KEYS from '../../../../utils/QueryKeys';
import LoadingOverlay from '../../../PackageBuilders/MultiPose/LoadingOverlay';
import useAccessCodePersister from '../../../../hooks/useAccessCodePersister';
import useRetouchingExtra from '../../../../hooks/useRetouchingExtra';
import usePricelist from '../../../../hooks/usePricelist';
import useMultiSeasonPoses from '../../../../hooks/useMultiSeasonPoses';
import usePoses from '../../../../hooks/usePoses';
import usePngPoses from '../../../../hooks/usePngPoses';
import useOfferCoupon from '../../../../hooks/useOfferCoupon';
import useLinkAttribution from '../../../../hooks/useLinkAttribution';
import PromoBanner from '../../../PromoBanner';
import GiftHeader, { useIsDesktop } from './GiftHeader';
import GiftFooter from './GiftFooter';
import CheckoutBar from './CheckoutBar';
import resolveOrderType, { ORDER_TYPES } from './orderType';
import useNumCartItems from '../../../../hooks/useNumCartItems';
import useCartAddonSheets from '../../../../hooks/useCartAddonSheets';

function useOrderPackageMutation(orderPackage, selectedPricelist) {
  const [indices, setIndices] = useState([]);

  const history = useHistory();
  const { customer, setCustomer } = useCustomer();
  // Third of three in this file that read `customer` unguarded while their
  // neighbours optional-chain it. Fixing two and leaving one is worse than
  // fixing none -- the next reader cannot tell which form is deliberate.
  const { data: pricelist } = useHolidayPricelist(customer?.currentSchool?.id);

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ addonSheets }) => {
      const { options } = pricelist;

      orderPackage.cartId = customer.cart.id;
      orderPackage.studentId = customer.currentStudent.id;
      // A standalone gifts package belongs to the GIFT pricelist — the same
      // one its $0 option below comes from. It previously took the browsed
      // pricelist (?pid=, possibly a senior pricelist), pairing a package from
      // one pricelist with an option from another: reporting misattributed
      // gift orders, and a senior-portraits pid would trip AddonSheet's
      // elementary-addon-on-senior-package validation. pid still drives pose
      // and background loading.
      orderPackage.packageId = orderPackage.packageId || pricelist.id;
      orderPackage.optionId = orderPackage.optionId || options[0].id;
      orderPackage.apiOption = orderPackage.apiOption || options[0];

      const newIndices = [];

      orderPackage.addonSheets = [...orderPackage.addonSheets, ...addonSheets].map((addonSheet, i) => {
        if (addonSheet.id) return addonSheet;

        const { addon, background, pose } = addonSheet;
        const params = {
          addonId: addon.id,
          backgroundId: background.id,
          index: i,
          poseId: pose.id,
        };

        newIndices.push(i);

        return new AddonSheet(params);
      });

      await orderPackage.save({ with: ['addonSheets'] });

      setIndices(newIndices);
      setCustomer({ ...customer, currentOrderPackage: orderPackage });
    },
    mutationKey: ['orderPackage', 'holiday'],
    onSuccess: async () => {
      if (orderPackage.errors.length > 0) return;

      const sheets = orderPackage.addonSheets.filter((addonSheet) => indices.includes(addonSheet.index));

      await queryClient.invalidateQueries([QUERY_KEYS.ORDER_PACKAGES]).then(() => history.push(routes.holiday.edit(orderPackage.id, { s: sheets.map((s) => s.id) })));
    },
  });
}

function HolidayBuilder() {
  const {
    access_code, sch, o, optional, pid,
  } = useQueryString();
  const { customer, setCustomer } = useCustomer();
  // Campaign gift links carry message_id; without this the purchase loses
  // its attribution to the send that brought the family here. Same cart
  // resolution as the order pages: the priority cart wins when present.
  useLinkAttribution(customer?.priorityCart || customer?.cart);
  const history = useHistory();
  const isDesktop = useIsDesktop();
  const { data: addons } = useGiftsPageAddons();
  const { data: selectedPricelist } = usePricelist(pid);
  const { data: pricelist, isFetching: isFetchingPricelist } = useHolidayPricelist(
    customer?.currentSchool?.id,
  );
  const { data: poses, isLoading } = selectedPricelist?.id && (selectedPricelist?.multipleSeasons?.length || selectedPricelist?.displaySharedPoses) ? (
    useMultiSeasonPoses(customer?.currentStudent?.id, selectedPricelist?.id)
  ) : (
    // ⚠️ OPTIONAL-CHAINED, like the branch above it. Unguarded, this threw
    // whenever there was no student in session -- React unmounted the tree and
    // the page went BLANK, with nothing in the console a customer or an
    // operator would notice.
    //
    // That is not a rare state: campaign gift links point AT this page (see
    // useLinkAttribution below, which exists to read their message_id), so a
    // parent whose session has lapsed clicks a link we paid to send and lands
    // on nothing. Reported 2026-09-06 -- reproducible in any incognito window.
    usePngPoses(customer?.currentStudent?.id, selectedPricelist?.id)
  );

  const { data: backgrounds } = useBackgrounds(pid || pricelist?.id, ['productTypeNames']);
  const { data: baseOrderPackage } = useOrderPackage(o);
  const { data: retouching, isLoading: isLoadingRetouching } = useRetouchingExtra();

  // Same promotional offer the package-selection screen shows, resolved for this
  // student + the pricelist (pid) so a senior sees their live offer (e.g. GRAD27
  // -- "20% off + 25 free grad announcements") while picking add-ons here too.
  const { data: offer } = useOfferCoupon(
    customer?.currentStudent?.studentId,
    customer?.currentSchool?.id,
    pid,
  );

  // Server-computed: does the cart currently have a senior portraits package
  // priced > $1? Gates the "Free" strike-through on announcement cards so a
  // customer who walks straight into /holiday without a package sees the real
  // price -- and matches what checkout will charge (Coupon.calculate_discount
  // uses the same predicate).
  //
  // refetchOnMount: 'always' is critical: the app-wide QueryClient sets
  // refetchOnMount: false, and nothing invalidates [QUERY_KEYS.CART, id] when
  // packages are added on other pages (the mutations invalidate ORDER_PACKAGES
  // only). Without this override, a customer who visits /holiday, adds a
  // qualifying senior package elsewhere, then returns to /holiday would see
  // the stale `qualifiesForFreeAnnouncements: false` and announcements would
  // stay at full price -- exactly the bug this branch fixes.
  const { data: cartWithQualification } = useCart(customer?.cart?.id, { refetchOnMount: 'always' });
  const qualifiesForFreeAnnouncements = !!cartWithQualification?.qualifiesForFreeAnnouncements;

  // ── what the bottom bar reports ────────────────────────────────────────────
  // `?optional=` is the order-flow signal. Everything
  // below is READ for display; none of it is recomputed here.
  const inOrderFlow = !!optional;

  // The cart's own subtotal, straight off Api::CartResource -- which reads
  // Cart#subtotal, the same figure checkout charges. `subtotal` is a plain
  // attribute there, not an extra_attribute, so the show endpoint this hook
  // already calls returns it and the bar costs no additional request.
  const subtotal = Number(cartWithQualification?.subtotal) || 0;

  // Total items in the cart, for the direct-visit variant. Server-side stats
  // count -- the same one the header badge shows, so the two cannot disagree.
  const { data: numCartItems } = useNumCartItems({
    id: customer?.cart?.id,
    email: customer?.cart?.email || customer?.email,
  });
  const cartCount = Number(numCartItems) || 0;

  // The bar is absent for a direct visitor with an empty cart, and the page's
  // bottom padding has to follow it: padding for a bar that is not there leaves
  // a hole under the last row of products.
  const showCheckoutBar = inOrderFlow || cartCount > 0;

  // ItemAdder owns which of the two screens is showing; CheckoutBar needs to
  // know, because on a phone ItemBuilder puts its own fixed bar at the bottom
  // and the two stack. Lifted here rather than guessed from the URL: a product
  // screen has no url of its own, it is component state.
  const [productScreenOpen, setProductScreenOpen] = useState(false);

  // WHICH PRODUCTS ARE ALREADY IN THE CART, from the server — so a tile still
  // reads "Added to order" after a refresh, a Back, or a return from the edit
  // screen. Nothing here is remembered in React state, because state does not
  // survive a reload and would tell a customer they had not bought something
  // they had.
  const { data: addedAddons } = useCartAddonSheets({
    studentId: customer?.currentStudent?.id,
    cartId: customer?.cart?.id,
  });

  // Pressing "Added to order" opens that add-on on the edit screen, where it can
  // be changed or removed — the same destination adding one lands on, so the
  // customer arrives somewhere they have already seen. It never adds a second
  // copy: a button that quietly did is how a cart ends up with four coffee cups.
  const handleOpenAdded = (added) => {
    if (!added?.orderPackageId) return;

    history.push(routes.holiday.edit(added.orderPackageId, { s: added.sheetIds }));
  };

  // WHICH BANNER THE SHELF SHOWS. One resolution point for the whole page --
  // see orderType.js for why the season leads and the school only breaks ties.
  // Senior-ness inside it delegates to the same `gradAddonEligible` that decides
  // which products this customer may see, so the banner and the shelf can never
  // disagree about who is shopping.
  const orderType = resolveOrderType({
    pricelistSeason: selectedPricelist?.season,
    schoolTypeId: customer?.currentSchool?.schoolTypeId,
    poses,
  });

  // ⚠️ SURFACED, NOT SWALLOWED. An unresolved order type means the customer is
  // looking at placeholder copy, which is a content bug rather than a crash and
  // would otherwise be invisible until someone noticed the banner reading
  // "PLACEHOLDER COPY". Dev-only so it cannot chatter in a customer's console.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (orderType !== ORDER_TYPES.UNKNOWN) return;

    // eslint-disable-next-line no-console
    console.warn(
      '[gifts] No order type resolved — the hero is showing PLACEHOLDER copy. '
      + `pricelistSeason=${selectedPricelist?.season || '(none)'} `
      + `schoolTypeId=${customer?.currentSchool?.schoolTypeId ?? '(none)'} `
      + `poses=${poses?.length || 0}`,
    );
  }, [orderType, selectedPricelist?.season]);

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  // HIDE THE SHARED SITE NAV WHILE THIS PAGE IS UP, so GiftHeader can stand in
  // its place. Hidden from the DOM rather than removed at the source: `Nav` is
  // rendered by CustomerApp for all 72 `/c/*` routes, and teaching that root
  // about this one page would put page knowledge in the app shell. This keeps
  // every trace of the swap inside the page, in the same shape as the
  // `.rails-nav` effect above it.
  //
  // AT EVERY WIDTH NOW. GiftHeader used to render only on desktop, so below it
  // the shared mobile nav had to stay or the page lost its navigation entirely.
  // It now carries a phone header of its own -- hamburger, logo, cart -- so the
  // shared one goes in both bands and the page never shows two headers.
  // Selected by "a <nav> that is not inside this page" because the shared
  // component's own class names are CSS-module hashes.
  //
  // ⚠️ `!important`, AND IT HAS TO BE. Nav.module.scss declares the nav's
  // `display` as `grid !important`, which beats a plain inline style: setting
  // `node.style.display = 'none'` leaves the element sitting there, visibly, with
  // display:none in its style attribute and a computed display of grid. The
  // header rendered correctly under a site nav that had simply ignored being
  // hidden. setProperty with the priority is the only form that wins.
  //
  // Removed rather than reset on cleanup, so the nav's own stylesheet decides how
  // it lays out again instead of this effect remembering a value for it.
  useEffect(() => {
    const siteNavs = Array.from(document.querySelectorAll('nav'))
      .filter((node) => !node.closest('.holidayPage'));

    siteNavs.forEach((node) => { node.style.setProperty('display', 'none', 'important'); });

    return () => siteNavs.forEach((node) => { node.style.removeProperty('display'); });
  }, [isDesktop]);

  useHolidayCoupon();

  const [orderPackage, setOrderPackage] = useState(new OrderPackage());

  // Add-ons chosen for the package these gifts attach to.
  //
  // ⚠️ IT IS THE ORDER PACKAGE'S COUNT, NOT THE WHOLE CART'S, and the two can
  // differ. `useOrderPackage(o)` loads addonSheets; a cart holding add-ons on
  // some OTHER package does not count them here. That matches the label, which
  // says "Package + N add-ons" about the package in hand -- and it is the only
  // count available without a second request, because useCart's show endpoint
  // sideloads no order packages.
  //
  // Entry points that arrive WITHOUT `?o=` (OptionSelection,
  // SeniorPackageChooser) have no package loaded, so this reads 0 until the
  // customer adds something.
  const addonCount = orderPackage?.addonSheets?.length || 0;

  // Where "Continue to Cart" goes: on through the order flow, which ends at the
  // review screen. Deliberately NOT routes.cart() — retouching is still an
  // offer this customer has not been made yet, and jumping the queue to the
  // cart would silently drop that step from the flow. The label names the
  // destination the customer cares about; the route keeps the flow intact.
  const handleSkip = () => {
    if (retouching && o) {
      history.push(routes.retouching(o));
    } else {
      history.push(routes.review());
    }
  };

  useEffect(() => {
    if (!baseOrderPackage) return;

    setOrderPackage(baseOrderPackage);
  }, [baseOrderPackage?.id]);

  const { mutate, ...rest } = useOrderPackageMutation(orderPackage, selectedPricelist);

  const mutation = { mutate, ...rest };

  useHolidayCoupon();

  const onSelectBackground = (newSheets) => mutate({ addonSheets: newSheets });

  // NO STUDENT MEANS NO SESSION, and everything below needs one. Send them to
  // the access code screen, which is where they have to go anyway -- a dead
  // blank page tells them nothing and loses the visit.
  //
  // A redirect rather than a message because this page cannot be used at all
  // without a student; there is nothing here to read while they fetch a code.
  if (!customer?.currentStudent?.id) {
    return <Redirect to={routes.studentIdEntry()} />;
  }

  // ⚠️ NEITHER OF THESE MAY `return null`, AND BOTH USED TO. A null render here
  // is a blank white page: nothing for the customer to act on, nothing in the
  // browser console, and nothing in the server log -- every request 200s. It
  // cost most of a day to find, because it only appeared on a SECOND visit.
  // See useHolidayPricelist for why the first one worked.
  //
  // Still-loading and genuinely-absent are different states and get different
  // answers. `isFetching`, not `isLoading`: a disabled query (no school id yet)
  // reports isLoading forever in react-query v4, which would pin the overlay up
  // permanently instead of falling through to the message below.
  if (!pricelist && isFetchingPricelist) {
    return (
      <LoadingOverlay />
    );
  }

  // A gifts page with no gift pricelist cannot book a standalone order: the
  // mutation needs that package's id AND its $0 option, and may not borrow
  // either from the browsed pricelist (see its comment above -- that pairing
  // misattributed orders). So SAY SO, and keep the customer's way out: the
  // header, the footer, and the button that finishes the order they already
  // have. `addons` cannot be falsy -- useGiftsPageAddons carries
  // `placeholderData: []` -- so it needs no guard of its own.
  if (!pricelist) {
    return (
      <div className={`${styles.page} holidayPage min-h-screen bg-white`}>
        <GiftHeader />

        <div className="mx-auto max-w-[640px] px-4 py-20 text-center">
          <h1 className="m-0 text-[28px] font-extrabold text-gift-ink">
            Add-ons aren&apos;t available yet
          </h1>
          <p className="mx-auto mt-3 max-w-[480px] text-[16px]" style={{ color: '#3A4250' }}>
            We can&apos;t show announcements and photo gifts for this school right now.
            Your order is safe &mdash; you can finish checking out, or get in touch and
            we&apos;ll sort it out.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleSkip}
              className={'inline-flex h-[48px] items-center justify-center rounded-gift-pill border-0 '
              + 'bg-gift-accent px-7 text-[15px] font-bold text-white transition hover:bg-gift-accentHover'}
            >
              Continue to checkout
            </button>
            <a
              href={routes.frequentlyAskedQuestions()}
              className={'inline-flex h-[48px] items-center justify-center rounded-gift-pill '
              + 'border-[1.5px] border-solid border-gift-ink bg-white px-7 text-[15px] '
              + 'font-bold text-gift-ink no-underline'}
            >
              Contact us
            </a>
          </div>
        </div>

        <GiftFooter />
      </div>
    );
  }
  if (backgrounds.length === 0) {
    return (
      <LoadingOverlay />
    );
  }

  if (isLoading) {
    return (
      <LoadingOverlay />
    );
  }

  if (optional && isLoadingRetouching) {
    return (
      <LoadingOverlay />
    );
  }

  // Arriving with ?o= means the gifts attach to an existing (possibly paid)
  // package, which prices tiles at price_with. Until it loads we don't know
  // which price applies — never flash the standalone price_without.
  if (o && !baseOrderPackage) {
    return (
      <LoadingOverlay />
    );
  }

  return (
    /* WHITE GROUND, and so the products carry no card chrome — a white card on
       a white page is just padding. The artwork itself does the separating;
       see Item.jsx.

       Bottom padding clears the fixed bar: 88px of bar plus the page's own
       64px. ⚠️ MORE ON A PHONE, because the bar stacks there and measures 168px
       — 152px of padding left the last row of products under it. Without the
       bar the page keeps its ordinary footer gap rather than a hole. */
    <div
      className={`${styles.page} holidayPage min-h-screen bg-white ${showCheckoutBar ? `${productScreenOpen ? 'max-sm:pb-[104px] pb-[208px]' : 'pb-[208px]'} sm:pb-[152px]` : 'pb-16'}`}
    >
      <GiftHeader />

      {/* Full-bleed and sticky, so it stays OUTSIDE the content wrapper below. */}
      <PromoBanner offer={offer} />

      {/* The page heading is NOT here. It belongs to the catalog, so it lives in
          GiftShop — a product screen is its own page and should open on the
          product, not under a title for the shelf you just left. ItemAdder is
          what knows which of the two is showing. */}
      <div>
        <ItemAdder
          onProductScreenChange={setProductScreenOpen}
          mutation={mutation}
          addons={addons}
          orderPackage={orderPackage}
          backgrounds={backgrounds}
          options={selectedPricelist?.options || pricelist.options}
          poses={poses}
          onSelectBackground={onSelectBackground}
          offer={offer}
          qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
          hasFreeMagnets={!!cartWithQualification?.hasFreeMagnets}
          // Senior-ness for the grad-announcement gate comes from the pricelist the
          // customer arrived with (seniors2027 -> senior), not from pose-array indexes.
          pricelistSeason={selectedPricelist?.season}
          // Presentation only: the catalog drops its breadcrumb for a customer
          // who was SENT here by the order flow rather than navigating to a shop.
          addedAddons={addedAddons || new Map()}
          inOrderFlow={!!optional}
          onOpenAdded={handleOpenAdded}
          orderType={orderType}
        />

      </div>

      <GiftFooter />

      {/* Fixed to the viewport, so its position in the document does not matter
          for layout -- last, so it is also last in the tab order rather than
          interrupting the shelf. Both actions push the flow's existing next
          step; see CheckoutBar for why they share it. */}
      <CheckoutBar
        productScreenOpen={productScreenOpen}
        inOrderFlow={inOrderFlow}
        addonCount={addonCount}
        subtotal={subtotal}
        cartCount={cartCount}
        onContinue={handleSkip}
        onSkip={handleSkip}
        onViewCart={() => history.push(routes.cart())}
      />

    </div>
  );
}

export default function LandableWrapper() {
  const { sch, sid, access_code: accessCode } = useQueryString();
  const { data: student } = useStudent({ studentId: sid, schoolId: sch });
  const { customer, setCustomer } = useCustomer();
  const { isLoading } = useAccessCodePersister({ accessCode, schoolId: sch });

  // Optional-chained to match `isSchoolSet` / `isStudentSet` below, which
  // already are. The context default is `{}` (createContext({})), so a render
  // outside the provider hands back undefined -- defensive rather than the
  // reported bug, but the same crash class as the student dereference this
  // commit fixes.
  useHolidayPricelist(customer?.currentSchool?.id);
  useGiftsPageAddons();

  useEffect(() => {
    if (!student) return;

    setCustomer({ ...customer, currentStudent: student, currentSchool: student.school });
  }, [student]);

  const isSchoolSet = customer?.currentSchool?.id?.toString() === sch?.toString();
  const isStudentSet = customer?.currentStudent?.studentId?.toString() === sid?.toString();

  const skipLandableCheck = !(sch || sid);

  if (accessCode && isLoading) {
    return (
      <LoadingOverlay />
    );
  }

  if (!skipLandableCheck && !(isSchoolSet && isStudentSet)) return null;

  return (
    <HolidayBuilder />
  );
}
